<template>
  <Head title="Mi cuenta" />
  <div class="container mx-auto p-4 sm:p-8">
    <div class="grid gap-4 grid-cols-1 sm:grid-cols-2 mb-8">
      <div>
        <h1 class="text-2xl font-bold">{{clientName}}</h1>
        <p class="text-gray-600">{{client?.companyName}}</p>
        <p class="text-gray-600">{{client?.identification}}</p>
        <Link class="text-red-500 hover:text-red-700" href="/logout" method="delete" as="button" preserve-scroll>Cerrar sesión</Link>
      </div>
      <div class="flex flex-col items-end p-4 bg-blue-100 rounded-lg">
        <p class="text-lg font-bold">Saldo vencido</p>
        <p class="text-blue-500 text-xl">${{formattedBalance}}</p>
      </div>
    </div>

    <div class="p-6 bg-white rounded-lg shadow-md">
      <div class="mb-4">
        <ul class="flex space-x-4">
          <li 
            v-for="(tab, index) in tabs" 
            :key="index" 
            :class="{ 'border-b-2 border-blue-500 text-blue-500': currentTab === index }" 
            @click="currentTab = index" 
            class="pb-2 cursor-pointer hover:text-blue-700 transition-colors"
          >
            {{ tab.title }}
          </li>
        </ul>
      </div>

      <div v-if="currentTab === 0">
        <form @submit.prevent="handleUpdateUser" class="space-y-4">
          <div class="grid gap-4 grid-cols-2">
            <div>
              <label for="first-name" class="block mb-2 text-gray-700 text-sm font-bold">Nombres:</label>
              <input 
                type="text" 
                id="first-name" 
                class="px-3 py-2 w-full text-gray-700 leading-tight border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                v-model="form.firstName"
                :class="{ 'border-red-500': errors.firstName }"
                required
              />
              <p v-if="errors.firstName" class="text-red-500 text-xs mt-1">{{ errors.firstName }}</p>
            </div>
            <div>
              <label for="last-name" class="block mb-2 text-gray-700 text-sm font-bold">Apellidos:</label>
              <input 
                type="text" 
                id="last-name" 
                class="px-3 py-2 w-full text-gray-700 leading-tight border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                v-model="form.lastName"
                :class="{ 'border-red-500': errors.lastName }"
                required
              />
              <p v-if="errors.lastName" class="text-red-500 text-xs mt-1">{{ errors.lastName }}</p>
            </div>
          </div>

          <div>
            <label for="email" class="block mb-2 text-gray-700 text-sm font-bold">Correo electrónico:</label>
            <input 
              type="email" 
              id="email" 
              class="px-3 py-2 w-full text-gray-700 leading-tight border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              v-model="form.email"
              :class="{ 'border-red-500': errors.email }"
              required
            />
            <p v-if="errors.email" class="text-red-500 text-xs mt-1">{{ errors.email }}</p>
          </div>

          <div>
            <label for="password" class="block mb-2 text-gray-700 text-sm font-bold">Contraseña: <span class="text-gray-500 font-normal">(Dejar en blanco si no desea cambiarla)</span></label>
            <input 
              type="password" 
              id="password" 
              class="px-3 py-2 w-full text-gray-700 leading-tight border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              v-model="form.password"
              :class="{ 'border-red-500': errors.password }"
              autocomplete="new-password"
            />
            <p v-if="errors.password" class="text-red-500 text-xs mt-1">{{ errors.password }}</p>
          </div>

          <div class="flex items-center justify-between">
            <button 
              type="submit" 
              class="px-4 py-2 text-white font-bold bg-blue-500 hover:bg-blue-700 rounded transition-colors"
              :disabled="processing"
            >
              {{ processing ? 'Actualizando...' : 'Actualizar usuario' }}
            </button>
            <p v-if="updateSuccess" class="text-green-500 text-sm">¡Información actualizada con éxito!</p>
          </div>
        </form>
      </div>

      <div v-if="currentTab === 1">
        <div v-if="client?.credit" class="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
          <div class="p-3 sm:p-4 bg-gray-100 rounded-lg">
            <p class="text-gray-600 text-sm">Límite de crédito</p>
            <p class="text-lg font-bold">${{formattedCreditLimit}}</p>
          </div>
          <div class="p-4 bg-gray-100 rounded-lg">
            <p class="text-gray-600 text-sm">Término de crédito</p>
            <p class="text-lg font-bold">{{client.term_credit}} Días</p>
          </div>
          <div class="p-4 bg-gray-100 rounded-lg">
            <p class="text-gray-600 text-sm">Saldo disponible</p>
            <p class="text-lg font-bold">${{formattedAvailableBalance}}</p>
          </div>
        </div>
        <div v-else class="text-center py-8">
          <h3 class="text-xl text-gray-600">No tienes crédito disponible</h3>
          <p class="text-gray-500 mt-2">Contacta con un asesor para solicitar crédito</p>
        </div>
      </div>

      <div v-if="currentTab === 2">
        <orders-table :orders="orders" />
      </div>
    </div>
  </div>
</template>

<script>
import WebLayout from '../../Layouts/WebLayout.vue'
import OrdersTable from './components/OrdersTable.vue'
import { Link } from '@inertiajs/inertia-vue3'
import { useForm, Head } from '@inertiajs/inertia-vue3'
export default {
  components: { OrdersTable, Link, Head },
  layout: WebLayout,
  props: {
    balance: {
      type: Number,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    auth: {
      type: Object,
      required: true
    },
    orders: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentTab: 2,
      tabs: [{ title: 'Información de la cuenta' }, { title: 'Detalles de crédito' }, { title: 'Pedidos' }],
      processing: false,
      updateSuccess: false,
      errors: {},
      form: useForm({
        firstName: this.client?.name?.split(' ')[0] || '',
        lastName: this.client?.name?.split(' ')[1] || '',
        email: this.client?.email || '',
        password: ''
      })
    }
  },
  computed: {
    clientName() {
      return this.client?.name || 'Usuario'
    },
    formattedBalance() {
      return this.formatCurrency(this?.balance || 0)
    },
    formattedCreditLimit() {
      return this.formatCurrency(this.client?.credit_limit || 0)
    },
    formattedAvailableBalance() {
      return this.formatCurrency(this.client?.credit_term || 0)
    },
    hasBalance() {
      return (this.client?.balance || 0) > 0
    }
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    async handleUpdateUser() {
      this.processing = true
      this.errors = {}
      this.updateSuccess = false

      try {
        await this.form.post('/api/user/update', {
          preserveScroll: true,
          onSuccess: () => {
            this.updateSuccess = true
            this.form.password = ''
            setTimeout(() => {
              this.updateSuccess = false
            }, 3000)
          },
          onError: (errors) => {
            this.errors = errors
          }
        })
      } catch (error) {
        console.error('Error updating user:', error)
      } finally {
        this.processing = false
      }
    },
    async handlePayment() {
      if (!this.hasBalance) return
      
      try {
        // Redirect to payment page or open payment modal
        this.$inertia.visit('/payment', {
          method: 'get',
          data: {
            amount: this.client.balance
          }
        })
      } catch (error) {
        console.error('Error initiating payment:', error)
      }
    }
  }
}
</script>
